import { apiErrorText } from "../../../api/api-error.js";
import { portalText } from "../../../i18n/portal.js";
/**
 * The wizard's last step, after `createVoiceAgent`: put every attached document straight into
 * storage, confirm each one, then start the build (plan-003 RC-32, design §16.8).
 *
 * Sequential on purpose: a menu and its per-language overlay are two files, and one at a time keeps
 * the progress a person reads honest ("2 of 2") and the per-version document count out of a race.
 *
 * RESUMABLE. A file that fails is marked `failed` with the API's (or the storage's) sentence and the
 * run stops; pressing the button again re-runs only what is not yet `confirmed`, each with a FRESH
 * link and grant (never a replay), and the VoiceAgent is not created a second time — the caller
 * keeps it. Starting the build is the last act, and an answer of `build_already_started` means the
 * goal already holds (a retry after a lost reply), so it is not a failure.
 *
 * AND THE MENU PREVIEW (plan-015 PRV, gap G6, owner answer 10, design §28.5.12): as soon as the menu is attached, it is
 * staged for the factory to read once, and the upload card shows what was understood — the items, the extras and the
 * first items with their prices — before anything is built ({@link watchMenuPreview}). The build later uploads the
 * same file and reuses that reading; a replaced file is read anew. A preview never blocks the wizard.
 */
import { ApiError } from "@wetalk/api-client";

import type { ConsoleGateway } from "../../../api/gateway.js";
import { DocumentUploadError, isBuildAlreadyStarted } from "../../../api/slice/agent-document.js";
import type { DocumentPreviewRequestView, DocumentPreviewSampleView, DocumentPreviewView } from "../../../api/slice/document-preview.js";

/**
 * One file a person attached, keyed by the template field it answers — or, for an upload that takes several files, by
 * its slot `<field code>/<file name>` (plan-018 WAPP-A), so each file has its own progress and its own confirmation.
 */
export interface AttachedDocument {
  readonly fieldCode: string;
  readonly file: File;
}

export type DocumentUploadPhase = "waiting" | "uploading" | "confirmed" | "failed";

export interface DocumentUploadProgress {
  readonly phase: DocumentUploadPhase;
  /** 0…1, from the PUT's own upload events. */
  readonly fraction: number;
  /** The reason a file failed, in words a person can act on; `null` otherwise. */
  readonly message: string | null;
}

export type DocumentUploadGateway = Pick<ConsoleGateway, "uploadAgentDocument" | "startVoiceAgentBuild">;

/**
 * Uploads every attached file not already confirmed, then starts the build. Returns once the build
 * was started (or already was). Raises the first file's failure after marking it.
 */
export async function uploadDocumentsThenStart(
  gateway: DocumentUploadGateway,
  voiceAgentId: string,
  attached: readonly AttachedDocument[],
  confirmed: ReadonlySet<string>,
  report: (fieldCode: string, progress: DocumentUploadProgress) => void,
  /** plan-018 WAPP-A: the words for a failed file — the wizard says the information refusals in the portal's language. */
  failureTextOf: (raised: Error) => string = (raised) => apiErrorText(raised, ""),
): Promise<void> {
  for (const one of attached) {
    if (confirmed.has(one.fieldCode)) continue;
    report(one.fieldCode, { phase: "uploading", fraction: 0, message: null });
    try {
      await gateway.uploadAgentDocument(voiceAgentId, one.file, (fraction) => {
        report(one.fieldCode, { phase: "uploading", fraction, message: null });
      });
    } catch (raised) {
      report(one.fieldCode, {
        phase: "failed",
        fraction: 0,
        get message() { return raised instanceof Error ? failureTextOf(raised) : portalText("operations", "the_file_could_not_be_uploaded_b0d8d82c"); },
      });
      throw raised;
    }
    report(one.fieldCode, { phase: "confirmed", fraction: 1, message: null });
  }

  try {
    await gateway.startVoiceAgentBuild(voiceAgentId);
  } catch (raised) {
    if (!isBuildAlreadyStarted(raised)) throw raised;
  }
}

/** "menu-autumn.pdf · uploading 42%" — the review step's line for one file. */
export function progressText(filename: string, progress: DocumentUploadProgress | undefined): string {
  if (progress === undefined || progress.phase === "waiting") return filename;
  if (progress.phase === "uploading") return portalText("operations", "value_uploading_value_f1daee22", { v0: String(filename), v1: String(String(Math.round(progress.fraction * 100))) });
  if (progress.phase === "confirmed") return portalText("operations", "value_uploaded_ab1af1fd", { v0: String(filename) });
  return portalText("operations", "value_failedvalue_d17c1462", { v0: String(filename), v1: String(progress.message === null ? "" : `: ${progress.message}`) });
}

// ── plan-015 PRV: the menu preview, before the build (gap G6, owner answer 10, design §28.5.12) ─────────────────────

/** The reference schema whose menu has a preview — the delivery template's. Read from the card, never from its code. */
export const MENU_PREVIEW_REFERENCE_SCHEMA = "pizzabot-menu-md-v1";

/** The upload field that carries the menu itself (the delivery field set's `menu`). */
export const MENU_PREVIEW_FIELD_CODE = "menu";

/** How often a preview being read is asked again, and for how long at most (the factory reads in well under that). */
export const MENU_PREVIEW_POLL_MS = 1500;
export const MENU_PREVIEW_POLL_LIMIT = 120;

/** What the upload card shows under the menu. Never blocking: the person can always build. */
export type MenuPreviewState =
  | { readonly phase: "reading" }
  | { readonly phase: "done"; readonly preview: DocumentPreviewView }
  | { readonly phase: "failed"; readonly message: string };

export type MenuPreviewGateway = Pick<ConsoleGateway, "previewDocument" | "readDocumentPreview">;

/** The sentence a preview that could not be asked for (or read in time) shows. The API's own words when it gave some. */
const MENU_PREVIEW_UNAVAILABLE = () => (portalText("operations", "we_couldn_t_read_the_menu_from_this_file_you_can_still_bui_2cd8dd92"));

/**
 * Stages the menu for its preview and follows it until it is read (or fails, or takes too long), reporting each state.
 * Stops — reporting nothing more — once `stopped()` says so (the file was replaced, or the card went away).
 */
export async function watchMenuPreview(
  gateway: MenuPreviewGateway,
  request: DocumentPreviewRequestView,
  report: (state: MenuPreviewState) => void,
  stopped: () => boolean,
  pause: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<void> {
  const settle = (preview: DocumentPreviewView): boolean => {
    if (preview.state === "done") {
      report({ phase: "done", preview });
      return true;
    }
    if (preview.state === "failed") {
      report({ phase: "failed", get message() { return preview.failureReason ?? MENU_PREVIEW_UNAVAILABLE(); } });
      return true;
    }
    return false;
  };
  try {
    report({ phase: "reading" });
    let preview = await gateway.previewDocument(request);
    if (stopped() || settle(preview)) return;
    for (let asked = 0; asked < MENU_PREVIEW_POLL_LIMIT; asked += 1) {
      await pause(MENU_PREVIEW_POLL_MS);
      if (stopped()) return;
      preview = await gateway.readDocumentPreview(preview.documentPreviewId);
      if (stopped() || settle(preview)) return;
    }
    report({ phase: "failed", get message() { return MENU_PREVIEW_UNAVAILABLE(); } });
  } catch (raised) {
    if (stopped()) return;
    // The API's sentence (a type it cannot read, too many menus this hour) is for the person; anything else is not.
    report({ phase: "failed", get message() { return raised instanceof ApiError || raised instanceof DocumentUploadError ? apiErrorText(raised, "") : MENU_PREVIEW_UNAVAILABLE(); } });
  }
}

/** "€8", "€9.50" — a menu price as the design writes it. */
export function menuPriceText(priceMinor: number): string {
  const whole = Math.floor(priceMinor / 100);
  const cent = priceMinor % 100;
  return cent === 0 ? `€${String(whole)}` : `€${String(whole)}.${String(cent).padStart(2, "0")}`;
}

/** "Small €8 · Medium €10 · Large €12", or "€4.50" for an item with one price. */
export function menuSamplePriceText(sample: DocumentPreviewSampleView): string {
  return sample.sizePrice.length === 0
    ? menuPriceText(sample.priceMinor)
    : sample.sizePrice.map((one) => `${one.size} ${menuPriceText(one.priceMinor)}`).join(" · ");
}

/** "We found 42 dishes and 11 extras (toppings and add-ons)." — the card's head (the export of 2026-10-10). */
export function menuPreviewHeadText(preview: DocumentPreviewView): string {
  const item = preview.itemCount ?? 0;
  const extra = preview.extraCount ?? 0;
  const dishes = portalText("operations", "merge.wizard.menu_dishes", { count: item });
  if (extra === 0) return portalText("operations", "merge.wizard.we_found_v0.24e659", { v0: String(dishes) });
  return portalText("operations", "merge.wizard.we_found_v0_and_v1_v2_toppings_and_add_ons.358d8f", { v0: String(dishes), v1: String(String(extra)), v2: portalText("operations", extra === 1 ? "extra_b43c4b82" : "extras_3fc771b9") });

}

/** "and 38 more. Something wrong? Fix your file and upload it again." — the card's foot (the export of 2026-10-10). */
export function menuPreviewFootText(preview: DocumentPreviewView): string {
  const more = (preview.itemCount ?? 0) - preview.sample.length;
  const fix = portalText("operations", "merge.wizard.something_wrong_fix_your_file_and_upload_it_again.e12f5a");
  return more > 0 ? portalText("operations", "merge.wizard.and_v0_more_v1.ca7a48", { v0: String(String(more)), v1: String(fix) }) : fix;

}
